/**
 * A list's filters, remembered for when somebody comes back to it (issue #86).
 *
 * The filters live in the URL, so a filtered list is a link and the back button
 * works. But every OTHER way back to a list - the breadcrumb on an element's
 * page, the sidebar, the redirect after a delete - is a link to the bare path,
 * and the bare path means "no filter". Open a requirement from a filtered list,
 * change it, click "Requirements", and the filter was gone: the whole list again,
 * to be narrowed by hand every time.
 *
 * So the URL stays the only authority on what a list is showing, and this adds
 * a memory BEHIND it: the last filter query seen on each list, kept per tab in
 * sessionStorage. Arriving at a list with no filter in the URL puts the
 * remembered one back, as a replace, so the URL still says what is on screen.
 *
 * The rules, and why:
 *
 *  - **An explicit URL wins.** A pasted link that carries ANY of the filter keys
 *    is taken exactly as it is - nothing remembered is merged into it - and
 *    becomes what is remembered. A link means what it says.
 *  - **Only on ARRIVAL.** Clearing the filters while on the list leaves the bare
 *    path too, and that must stay cleared; it is remembered as "nothing".
 *    Restoring there would make the '✕' a button that does not work.
 *  - **Per program and per path.** One program's subsystem names are another's
 *    empty list, and '/ports' and '/functions' are different questions even
 *    though they are one mounted component.
 *  - **sessionStorage, not localStorage.** Per tab, so two tabs on two filters
 *    do not overwrite each other, and gone when the tab is: a filter from last
 *    week silently narrowing today's list is the opposite of the bug this fixes.
 */

import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { getProjectSlug } from '../../api'
import { QUICK_KEY } from './quickFilter'
import { FILTER_KEY, SEARCH_KEY, SORT_KEY } from './rowFilter'

/**
 * The query-string keys that ARE a list's filters, and so are remembered.
 *
 * The search box, the quick filters, the column conditions and the sort - what
 * decides which rows are shown and in what order. Not the page number, and not
 * a tab: those are where somebody was, not what they asked.
 */
export const REMEMBERED_KEYS = [SEARCH_KEY, QUICK_KEY, FILTER_KEY, SORT_KEY] as const

/** Where one list's filters are kept. */
export function storageKey(project: string, path: string): string {
  return `best.filters:${project}:${path}`
}

/**
 * The filter part of a query string, as a query string; '' for none.
 *
 * In the order of REMEMBERED_KEYS rather than the URL's, so two URLs asking the
 * same thing remember the same text.
 */
export function filterQuery(search: string): string {
  const params = new URLSearchParams(search)
  const kept = new URLSearchParams()
  for (const key of REMEMBERED_KEYS) {
    const value = params.get(key)
    if (value !== null) kept.set(key, value)
  }
  return kept.toString()
}

/**
 * The query string to put back on arrival, or null to leave the URL alone.
 *
 * Null when the URL already names a filter (an explicit URL wins) or nothing is
 * remembered. Anything else on the URL that is not a filter is kept beside what
 * comes back: this owns the filter keys, not the query string.
 *
 * Pure, and exported so the rules are tested as a table.
 */
export function restoredQuery(search: string, remembered: string | null): string | null {
  if (filterQuery(search) !== '' || !remembered) return null

  const next = new URLSearchParams(search)
  for (const [key, value] of new URLSearchParams(remembered)) {
    if ((REMEMBERED_KEYS as readonly string[]).includes(key)) next.set(key, value)
  }
  const text = next.toString()
  return text === new URLSearchParams(search).toString() ? null : text
}

/**
 * Storage that may refuse: private browsing and locked-down profiles throw on
 * sessionStorage, and a list that cannot remember must still be a list.
 */
function recall(key: string): string | null {
  try {
    return sessionStorage.getItem(key)
  } catch {
    return null
  }
}

function remember(key: string, query: string): void {
  try {
    if (query) sessionStorage.setItem(key, query)
    else sessionStorage.removeItem(key)
  } catch {
    // Not remembering is the whole of the failure; the list itself is fine.
  }
}

/**
 * Remember this list's filters, and put them back when it is arrived at bare.
 *
 * Called by the screen that OWNS a list (the requirements screen, the generic
 * table, parts), once. The screen keeps reading its filters from the URL as it
 * always has; this only ever writes the URL on arrival.
 */
export function useRememberedFilters(): void {
  const { pathname, search } = useLocation()
  const navigate = useNavigate()
  const key = storageKey(getProjectSlug(), pathname)

  /** The list last arrived at; a different one is a new arrival. */
  const arrived = useRef<string | null>(null)
  /**
   * Whether a restore has been asked for and the URL is still bare.
   *
   * Between the restore and its landing the URL has no filter, and remembering
   * THAT would wipe the very filter being put back - which a re-run effect (as
   * StrictMode does) would otherwise do. Any filter at all ends it, so a restore
   * that never lands cannot stop the list being remembered.
   */
  const restoring = useRef(false)

  useEffect(() => {
    const asked = filterQuery(search)

    if (arrived.current !== key) {
      arrived.current = key
      restoring.current = false
      const back = restoredQuery(search, recall(key))
      if (back !== null) {
        restoring.current = true
        navigate({ pathname, search: `?${back}` }, { replace: true })
        return
      }
    }

    if (restoring.current) {
      if (asked === '') return
      restoring.current = false
    }
    remember(key, asked)
  }, [key, pathname, search, navigate])
}
